"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InventoryClient = exports.InventoryError = void 0;
const lru_cache_1 = require("lru-cache");
const _ = require("lodash");
const business_types_1 = require("@deliverr/business-types");
Object.defineProperty(exports, "InventoryError", { enumerable: true, get: function () { return business_types_1.InventoryError; } });
const base_client_1 = require("@deliverr/base-client");
const errors_1 = require("@deliverr/errors");
const _1 = require(".");
const ProcessedInventoryMovement_1 = require("./ProcessedInventoryMovement");
const commons_utils_1 = require("@deliverr/commons-utils");
const ApiErrorSerializers_1 = require("@deliverr/commons-logs/lib/ApiErrorSerializers");
const commons_logs_1 = require("@deliverr/commons-logs");
const INVENTORY_SERVICE_URL = process.env.INVENTORY_SERVICE_URL;
const INVENTORY_BASIC_AUTH_USER = process.env.INVENTORY_BASIC_AUTH_USER;
const INVENTORY_BASIC_AUTH_PASSWORD = process.env.INVENTORY_BASIC_AUTH_PASSWORD;
const INVENTORY_CLIENT_BATCH_LIMIT = (0, commons_utils_1.getEnvNumber)("INVENTORY_CLIENT_BATCH_LIMIT", 100);
class InventoryClient extends base_client_1.ApiBasicAuthClient {
    constructor(config = {}, basicAuthUser, basicAuthPassword) {
        super(Object.assign(Object.assign({}, config), { baseURL: `${config.baseURL || INVENTORY_SERVICE_URL}/api/v1/inventory`, basicAuthPassword: basicAuthUser || INVENTORY_BASIC_AUTH_PASSWORD, basicAuthUser: basicAuthPassword || INVENTORY_BASIC_AUTH_USER }));
        if (!INVENTORY_SERVICE_URL) {
            commons_logs_1.log.warn("INVENTORY_SERVICE_URL environment variable is not set");
        }
        if (!INVENTORY_BASIC_AUTH_USER || !INVENTORY_BASIC_AUTH_PASSWORD) {
            commons_logs_1.log.warn("INVENTORY_BASIC_AUTH_USER and INVENTORY_BASIC_AUTH_PASSWORD environment variable is not set");
        }
        this.warehouseATPCache = new lru_cache_1.LRUCache({ max: 20000 });
    }
    async createStockKeepingUnit(sellerId, dsku) {
        try {
            return await this.post("/stockkeepingunits/", undefined, {
                seller: sellerId,
                dsku,
            });
        }
        catch (ex) {
            switch (ex.response.status) {
                case 409:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.DSKU_ALREADY_EXISTS);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async createStockKeepingUnits(sellerId, skus) {
        try {
            return await this.post("/stockkeepingunits/create-skus", { skus }, {
                seller: sellerId,
            });
        }
        catch (ex) {
            switch (ex.response.status) {
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async getAvailableSkusForSeller(sellerId) {
        try {
            return await this.get(`/stockkeepingunits/available/${sellerId}`, {});
        }
        catch (ex) {
            switch (ex.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                case 404:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.UNKNOWN_SELLER);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async getInventorySummary(sellerId) {
        try {
            return await this.get(`/stockkeepingunits/summary/${sellerId}`, {});
        }
        catch (ex) {
            switch (ex.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                case 404:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.UNKNOWN_SELLER);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async getATP(dskus, getAgingData, poolName) {
        try {
            const dskuChunks = _.chunk(dskus, INVENTORY_CLIENT_BATCH_LIMIT);
            let result = {};
            for (const dskuChunk of dskuChunks) {
                const queryParams = poolName
                    ? { dskus: dskuChunk.join(","), getAgingData, poolName }
                    : { dskus: dskuChunk.join(","), getAgingData };
                const chunkResult = await this.get("/stockkeepingunits/atp/", queryParams);
                result = Object.assign(Object.assign({}, result), chunkResult);
            }
            return result;
        }
        catch (ex) {
            switch (ex.response.status) {
                case 404:
                    return {};
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async getATPsApproaching365(poolName, maxResults, offset) {
        try {
            return await this.get("/stockkeepingunits/atpApproaching365", {
                poolName,
                maxResults,
                offset,
            });
        }
        catch (ex) {
            switch (ex.response.status) {
                case 404:
                    return {};
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async getAllSellerATP(seller, maxResults, offset, poolName) {
        try {
            const params = poolName ? { seller, maxResults, offset, poolName } : { seller, maxResults, offset };
            return await this.get("/stockkeepingunits/atp/all", params);
        }
        catch (ex) {
            switch (ex.response.status) {
                case 404:
                    return {};
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async hasHadInventory(dsku) {
        try {
            await this.get(`/stockkeepingunits/${dsku}/hadInventory`);
            return true;
        }
        catch (ex) {
            switch (ex.response.status) {
                case 404:
                    return false;
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async getWarehouseATP(skus, poolName) {
        try {
            if (poolName) {
                return await this.post(`/stockkeepingunits/atp/warehouse?poolName=${poolName}`, {
                    skus,
                });
            }
            return await this.post("/stockkeepingunits/atp/warehouse", {
                skus,
            });
        }
        catch (ex) {
            switch (ex.response.status) {
                case 404:
                    return {};
                default:
                    throw new errors_1.DeliverrError({
                        code: ex.response.data.code,
                        functionName: "getWarehouseATP",
                        payload: ex.response.data,
                    });
            }
        }
    }
    async getWarehouse(warehouseId) {
        try {
            return await this.get("/warehouse", { warehouseId });
        }
        catch (ex) {
            switch (ex.response.status) {
                case 404:
                    return undefined;
                default:
                    throw new errors_1.DeliverrError({
                        code: ex.response.data.code,
                        functionName: "getWarehouse",
                        payload: ex.response.data,
                    });
            }
        }
    }
    async createOrUpdateWarehouse(warehouse) {
        try {
            return await this.post("/warehouse", warehouse);
        }
        catch (ex) {
            switch (ex.response.status) {
                case 404:
                    return undefined;
                default:
                    throw new errors_1.DeliverrError({
                        code: ex.response.data.code,
                        functionName: "getWarehouse",
                        payload: ex.response.data,
                    });
            }
        }
    }
    async getWarehouseATPCached(skus) {
        const warehouseATP = {};
        const uncachedSkus = [];
        for (const sku of skus) {
            const skuWarehouseAtp = this.warehouseATPCache.get(sku);
            if (skuWarehouseAtp) {
                warehouseATP[sku] = skuWarehouseAtp;
            }
            else {
                uncachedSkus.push(sku);
            }
        }
        if (uncachedSkus.length === 0) {
            return _.pickBy(await (0, commons_utils_1.asyncHash)(warehouseATP), _.identity);
        }
        const promise = this.post("/stockkeepingunits/atp/warehouse", {
            skus: uncachedSkus,
        }).catch((ex) => {
            switch (ex.response.status) {
                case 404:
                    return {};
                default:
                    throw new errors_1.DeliverrError({
                        code: ex.response.data.code,
                        functionName: "getWarehouseATP",
                        payload: ex.response.data,
                    });
            }
        });
        for (const sku of uncachedSkus) {
            const skuPromise = promise.then((apiResult) => apiResult[sku]);
            this.warehouseATPCache.set(sku, skuPromise);
            warehouseATP[sku] = skuPromise;
        }
        return _.pickBy(await (0, commons_utils_1.asyncHash)(warehouseATP), _.identity);
    }
    async auditSkus(dskus) {
        try {
            return await this.put("/stockkeepingunits/audit", undefined, {
                dskus: dskus.join(","),
            });
        }
        catch (ex) {
            commons_logs_1.log.warn("error auditing skus " + dskus, ex.response.status);
            throw new errors_1.DeliverrError({
                code: errors_1.ApiError.UNKNOWN_ERROR,
                payload: (0, ApiErrorSerializers_1.apiErrorSerializer)(ex),
            });
        }
    }
    async getAvailableATP(seller, maxResults, offset, poolName) {
        try {
            const params = poolName ? { seller, maxResults, offset, poolName } : { seller, maxResults, offset };
            return await this.get("/stockkeepingunits/atp/available", params);
        }
        catch (ex) {
            switch (ex.response.status) {
                case 404:
                    return {};
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async applyWarehouseInventoryMovement(movementOrWarehouseId, dsku, delta, movementId, reason = ProcessedInventoryMovement_1.MovementReason.ADJUSTMENT) {
        let movement;
        if (typeof movementOrWarehouseId === "string" && dsku !== undefined) {
            movement = {
                delta,
                movementId,
                warehouseId: movementOrWarehouseId,
                dsku,
                reason,
            };
        }
        else {
            movement = movementOrWarehouseId;
        }
        try {
            return this.put("/stockkeepingunits/onhandqty/warehousemovement", movement);
        }
        catch (ex) {
            switch (ex.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                case 404:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.UNKNOWN_SKU);
                case 409:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.INSUFFICIENT_INVENTORY_AVAILABLE);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async applyInventoryEvent(event) {
        return this.put("/stockkeepingunits/applyEvent", event);
    }
    async consumePoolInventory(orderRef, skus, poolName) {
        try {
            return await this.put("/stockkeepingunits/atp/consumed", new _1.InventoryOrder({ orderRef, lineItemDetails: skus, poolName }));
        }
        catch (ex) {
            switch (ex.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                case 404:
                    throw new business_types_1.InventoryItemError(business_types_1.InventoryError.UNKNOWN_SKU, ex);
                case 409:
                    throw new business_types_1.InventoryItemError(business_types_1.InventoryError.STOCKOUT, ex);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async restorePoolInventory(orderRef, skus, poolName) {
        try {
            return await this.put("/stockkeepingunits/atp/restored", new _1.InventoryOrder({ orderRef, lineItemDetails: skus, poolName }));
        }
        catch (ex) {
            switch (ex.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                case 404:
                    throw new Error(business_types_1.InventoryError.UNKNOWN_SKU);
                case 409:
                    throw new Error(business_types_1.InventoryError.INVENTORY_OUT_OF_SYNC);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async registerLineItemsForShipping(orderRef, shipmentId, warehouseId, skus, shipmentCreatedBy, orderType, poolName) {
        try {
            return await this.put("/stockkeepingunits/onhandqty/shipped", new _1.InventoryOrder({
                orderRef,
                lineItemDetails: skus,
                shipmentId,
                warehouseId,
                orderType,
                poolName,
                shipmentCreatedBy,
            }));
        }
        catch (ex) {
            switch (ex.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                case 404:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.UNKNOWN_SKU);
                case 409:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.INVENTORY_OUT_OF_SYNC);
                case 412:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.INSUFFICIENT_INVENTORY_AVAILABLE);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async unregisterLineItemsForShipping(orderRef, shipmentId, warehouseId, skus, shipmentCancellationReason, shipmentError, orderType, poolName) {
        try {
            return await this.put("/stockkeepingunits/onhandqty/unshipped", new _1.InventoryOrder({
                orderRef,
                lineItemDetails: skus,
                shipmentId,
                warehouseId,
                shipmentCancellationReason,
                shipmentError,
                orderType,
                poolName,
            }));
        }
        catch (ex) {
            switch (ex.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                case 404:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.UNKNOWN_SKU);
                case 409:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.INVENTORY_OUT_OF_SYNC);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async updateLineItemsForShipping(orderRef, shipmentId, warehouseId, lineItemDetails, poolName) {
        try {
            return await this.put("/stockkeepingunits/onhandqty/lineitem", new _1.InventoryOrder({
                orderRef,
                lineItemDetails,
                shipmentId,
                warehouseId,
                poolName,
            }));
        }
        catch (ex) {
            switch (ex.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                case 404:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.UNKNOWN_SKU);
                case 409:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.INVENTORY_OUT_OF_SYNC);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async getOrderLineItemSnapshot(orderRef, dskus) {
        try {
            return await this.post("/stockkeepingunits/reservation/orderlineitemsnapshot", {
                orderRef,
                dskus: dskus !== null && dskus !== void 0 ? dskus : null,
            });
        }
        catch (ex) {
            switch (ex.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                case 404:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.UNKNOWN_ORDER_REF);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    clearSkuATPCache(sku) {
        this.warehouseATPCache.delete(sku);
    }
    async summarizeInventoryReceipts(asnId, dsku) {
        try {
            return await this.get("/stockkeepingunits/received", {
                asnId,
                dsku,
            });
        }
        catch (ex) {
            switch (ex.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                case 500:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.UNKNOWN_SKU);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async getInventoryMovements(from, to) {
        const ctx = { fn: "getInventoryMovements", from, to };
        commons_logs_1.log.info(ctx);
        try {
            return await this.post("/movements", { from, to });
        }
        catch (ex) {
            return (0, base_client_1.handleApiError)(ex, { errorCode: _1.InventoryMovementsErrorCode.UNKNOWN }, () => ({ movements: [] }));
        }
    }
    async summarizeInventoryReceiptsPerWarehouse(asnId, dsku) {
        try {
            return await this.get("/stockkeepingunits/received/warehouse", {
                asnId,
                dsku,
            });
        }
        catch (ex) {
            switch (ex.response.status) {
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async getLostUnits(selectedPageIndex, pageSize) {
        try {
            return await this.get("/stockkeepingunits/lostunits", {
                selectedPageIndex,
                pageSize,
            });
        }
        catch (ex) {
            switch (ex.response.status) {
                case 404:
                    return {
                        lostUnitsTrackerEntries: [],
                        totalPages: 0,
                    };
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async getLostUnit(id) {
        try {
            return await this.get(`/stockkeepingunits/lostunits/${id}`);
        }
        catch (ex) {
            switch (ex.response.status) {
                case 404:
                    return {};
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async createLostUnits(entry) {
        try {
            return await this.post("/stockkeepingunits/lostunits", entry);
        }
        catch (ex) {
            throw this.getInventoryDeliverrError(ex);
        }
    }
    async filterLostUnits(selectedPageIndex, pageSize, dsku, shippingPlanId) {
        try {
            return await this.post("/stockkeepingunits/lostunits/filter", {
                dsku,
                shippingPlanId,
            }, {
                selectedPageIndex,
                pageSize,
            });
        }
        catch (ex) {
            switch (ex.response.status) {
                case 404:
                    return {
                        lostUnitsTrackerEntries: [],
                        totalPages: 0,
                    };
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async editLostUnits(entry) {
        try {
            return await this.put("/stockkeepingunits/lostunits", entry);
        }
        catch (ex) {
            throw this.getInventoryDeliverrError(ex);
        }
    }
    async deleteLostUnits(id) {
        try {
            await this.delete(`/stockkeepingunits/lostunits/${id}`);
        }
        catch (ex) {
            throw this.getInventoryDeliverrError(ex);
        }
    }
    async initiateWarehouseOffboard(warehouseTransfer) {
        try {
            return await this.post("/warehouse-offboard", warehouseTransfer, {});
        }
        catch (ex) {
            switch (ex.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async createWarehouseTransferAsn(transferKey, asnId, orderId, fromWarehouseNaturalId, toWarehouseNaturalId) {
        try {
            return await this.post("/warehouse-transfer-asn", {}, {
                transferKey,
                asnId,
                orderId,
                fromWarehouseNaturalId,
                toWarehouseNaturalId,
            });
        }
        catch (ex) {
            throw this.getInventoryDeliverrError(ex);
        }
    }
    async createWarehouseTransfer(transferKey, asnId, orderId, fromWarehouseId, toWarehouseId, warehouseTransferAsnItems) {
        try {
            return await this.post("/warehouse-transfer", {
                transferKey,
                asnId,
                orderId,
                fromWarehouseId,
                toWarehouseId,
                warehouseTransferAsnItems,
            }, {});
        }
        catch (ex) {
            switch (ex) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async getWarehouseTransferAsn(asnId) {
        try {
            return await this.get(`/warehouse-transfer-asn/${asnId}`);
        }
        catch (ex) {
            switch (ex.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                case 404:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.UNKNOWN_WAREHOUSE_TRANSFER_ASN);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async getWarehouseTransfer(transferKey) {
        try {
            return await this.get(`/warehouse-transfer/${transferKey}`);
        }
        catch (ex) {
            switch (ex.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                case 404:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.UNKNOWN_WAREHOUSE_TRANSFER_KEY);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async lostWarehouseTransfer(transferKey, skuQuantities) {
        try {
            return await this.post(`/warehouse-transfer/${transferKey}/lost`, {
                skuQuantities,
            });
        }
        catch (ex) {
            switch (ex.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                case 404:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.UNKNOWN_WAREHOUSE_TRANSFER_KEY);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async updateWarehouseTransferStatus(transferKey, status) {
        try {
            return await this.put(`/warehouse-transfer/${transferKey}/status`, status);
        }
        catch (ex) {
            switch (ex.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                case 404:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.UNKNOWN_WAREHOUSE_TRANSFER_KEY);
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async getWarehouseTransferAsnItems(asnId, offset, limit) {
        try {
            return await this.get(`/warehouse-transfer-asn/${asnId}/warehouse-transfer-asn-item`, {
                offset,
                limit,
            });
        }
        catch (ex) {
            switch (ex.response.status) {
                case 400:
                    throw new errors_1.DeliverrError(business_types_1.InventoryError.BAD_REQUEST);
                case 404:
                    return { content: [] };
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async getStockOutUnits(poolName, maxResults, offset) {
        try {
            return await this.get("/stockkeepingunits/stockOutUnits", {
                poolName,
                maxResults,
                offset,
            });
        }
        catch (ex) {
            switch (ex.response.status) {
                case 404:
                    return [];
                default:
                    throw this.getInventoryDeliverrError(ex);
            }
        }
    }
    async getInventoryExcludingNodeInventoryIds(warehouseId, poolName, nodeInventoryIdsToExclude) {
        try {
            return await this.post("/query-warehouseinventorydetails/by-wmsinventoryid", {
                warehouseId,
                poolName,
                skuOrWmsIdList: nodeInventoryIdsToExclude,
            });
        }
        catch (ex) {
            throw this.getInventoryDeliverrError(ex);
        }
    }
    async triggerAlgoliaSyncBySeller(sellerId, poolName) {
        try {
            const queryParams = poolName ? { poolName } : undefined;
            return await this.put(`/poolinventory/sellerId/${sellerId}/updated`, {}, queryParams);
        }
        catch (error) {
            throw this.getInventoryDeliverrError(error);
        }
    }
    async enableInventoryWarehouse(warehouseId, enabled) {
        const inventoryWarehouseConfig = { warehouseId, available: enabled };
        try {
            return await this.post(`/warehouse`, inventoryWarehouseConfig);
        }
        catch (error) {
            throw this.getInventoryDeliverrError(error);
        }
    }
    getInventoryDeliverrError(error) {
        return this.maybeGetDeliverrError(error) || this.maybeGetNetworkError(error) || this.getUnknownError(error);
    }
}
exports.InventoryClient = InventoryClient;
//# sourceMappingURL=InventoryClient.js.map